import React, { useState, useEffect } from 'react';
import { X, Edit2, Trash2, Package, DollarSign, Calendar, User, FileText, Plus } from 'lucide-react';
import { Purchase, PurchaseLineItem } from '../../types/purchase';
import { CategoryHierarchy } from '../../types/category';
import { getItemTrackingConfig } from '../../utils/categoryTracking';
import { purchasesAPI } from '../../services/purchasesAPI';
import { purchaseLineItemsAPI } from '../../services/purchaseLineItemsAPI';
import { productCatalogAPI, Product } from '../../services/productCatalogAPI';
import { inventoryInstancesAPI, BulkStock } from '../../services/inventoryInstancesAPI';
import { AddLineItemModal } from './AddLineItemModal';
import SerializedItemBatchModal from './SerializedItemBatchModal';
import BulkItemCreationModal from './BulkItemCreationModal';
import BulkRestockModal from './BulkRestockModal';
import BulkCreateFromCatalogModal from './BulkCreateFromCatalogModal';
import SerializedFromCatalogModal from './SerializedFromCatalogModal';
import AllocationProgressBar from './AllocationProgressBar';
interface PurchaseDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchase: Purchase | null;
  categories: CategoryHierarchy[];
  linkedItems?: any[]; // Inventory items created from this purchase
  onEdit?: () => void;
  onDelete?: () => void;
  onItemsCreated?: () => void; // Callback to refresh linked items
  onPurchaseUpdated?: () => void; // NEW: Callback to refresh purchase list
}

const PurchaseDetailModal: React.FC<PurchaseDetailModalProps> = ({
  isOpen,
  onClose,
  purchase,
  categories,
  linkedItems = [],
  onEdit,
  onDelete,
  onItemsCreated,
  onPurchaseUpdated // NEW
}) => {
  const [lineItems, setLineItems] = useState<PurchaseLineItem[]>([]);
  const [loadingLineItems, setLoadingLineItems] = useState(false);
  const [isAddingLineItem, setIsAddingLineItem] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState<PurchaseLineItem | null>(null);
  const [isCreatingSerializedItems, setIsCreatingSerializedItems] = useState(false);
  const [isCreatingBulkItem, setIsCreatingBulkItem] = useState(false);
  
  // New state for product catalog integration
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [existingBulkStock, setExistingBulkStock] = useState<BulkStock | null>(null);
  const [isRestockingBulk, setIsRestockingBulk] = useState(false);
  const [isCreatingBulkFromCatalog, setIsCreatingBulkFromCatalog] = useState(false);
  const [isCreatingSerializedFromCatalog, setIsCreatingSerializedFromCatalog] = useState(false);
  const [loadingProductData, setLoadingProductData] = useState(false);
  // Load line items when modal opens
  useEffect(() => {
    if (isOpen && purchase) {
      loadLineItems();
    }
  }, [isOpen, purchase?.purchaseId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLineItems([]);
      setIsAddingLineItem(false);
      setSelectedLineItem(null);
      setIsCreatingSerializedItems(false);
      setIsCreatingBulkItem(false);
      setSelectedProduct(null);
      setExistingBulkStock(null);
      setIsRestockingBulk(false);
      setIsCreatingBulkFromCatalog(false);
      setIsCreatingSerializedFromCatalog(false);
      setLoadingProductData(false);
    }
  }, [isOpen]);

  const loadLineItems = async () => {
    if (!purchase) return;
    
    setLoadingLineItems(true);
    try {
      const response = await purchaseLineItemsAPI.getLineItems(purchase.purchaseId);
      
      // Enrich with category names
      const enrichedLineItems = response.data.map(item => {
        const parentCat = categories.find(c => c.categoryId === item.categoryId);
        const subCat = categories.find(c => c.categoryId === item.subcategoryId);
        return {
          ...item,
          categoryName: parentCat?.name,
          subcategoryName: subCat?.name
        };
      });
      
      setLineItems(enrichedLineItems);
    } catch (error) {
      console.error('Failed to load line items:', error);
    } finally {
      setLoadingLineItems(false);
    }
  };

  const handleAddLineItemClick = () => {
    setIsAddingLineItem(true);
  };

  const handleLineItemAdded = (lineItem: PurchaseLineItem) => {
    setLineItems([...lineItems, lineItem]);
    setIsAddingLineItem(false);
    
    // NEW: Notify parent that purchase was updated (line item count changed)
    if (onPurchaseUpdated) {
      onPurchaseUpdated();
    }
  };

  const handleAddItemsFromLine = async (lineItem: PurchaseLineItem) => {
    setSelectedLineItem(lineItem);
    
    // Check if line item has productId (from Product Catalog)
    const productId = (lineItem as any).productId;
    
    if (productId) {
      // New flow: Use product catalog data
      setLoadingProductData(true);
      try {
        // Fetch product details
        const product = await productCatalogAPI.getProduct(productId);
        setSelectedProduct(product);
        
        if (product.productType !== 'SERIALIZED') {
          // Check for existing bulk stock
          const existingStock = await inventoryInstancesAPI.getExistingBulkStock(productId);
          
          if (existingStock) {
            // Existing stock found - show restock modal
            setExistingBulkStock(existingStock);
            setIsRestockingBulk(true);
          } else {
            // No existing stock - show create modal
            setIsCreatingBulkFromCatalog(true);
          }
       } else {
          // Serialized product - use catalog-aware modal
          setIsCreatingSerializedFromCatalog(true);
        }
      } catch (error) {
        console.error('Failed to load product data:', error);
        // Fallback to category-based detection
        const subcategory = categories.find(c => c.categoryId === lineItem.subcategoryId);
        if (subcategory) {
          const trackingConfig = getItemTrackingConfig(subcategory);
          if (trackingConfig.trackingType === 'SERIALIZED') {
            setIsCreatingSerializedItems(true);
          } else {
            setIsCreatingBulkItem(true);
          }
        }
      } finally {
        setLoadingProductData(false);
      }
    } else {
      // Legacy flow: Use category-based detection (shouldn't happen with new purchases)
      console.warn('Line item missing productId - using legacy category-based detection');
      const subcategory = categories.find(c => c.categoryId === lineItem.subcategoryId);
      if (subcategory) {
        const trackingConfig = getItemTrackingConfig(subcategory);
        if (trackingConfig.trackingType === 'SERIALIZED') {
          setIsCreatingSerializedItems(true);
        } else {
          setIsCreatingBulkItem(true);
        }
      }
    }
  };

  const handleItemsComplete = async (itemIds: string[]) => {
    // Items created successfully - update line item's allocatedQuantity
    if (itemIds.length > 0 && selectedLineItem && purchase) {
      try {
        await purchaseLineItemsAPI.incrementAllocatedQuantity(
          purchase.purchaseId,
          selectedLineItem.lineItemId,
          itemIds.length
        );
        console.log(`✅ Updated line item ${selectedLineItem.lineItemId} - allocated quantity increased by ${itemIds.length}`);
        
        // Reload line items to get updated allocation
        await loadLineItems();
        
        // NEW: Notify parent that purchase was updated (allocation changed)
        if (onPurchaseUpdated) {
          onPurchaseUpdated();
        }
      } catch (error) {
        console.error('Failed to update allocated quantity:', error);
      }
    }

    // Close modals
    setIsCreatingSerializedItems(false);
    setIsCreatingBulkItem(false);
    setSelectedLineItem(null);
    
    // Refresh linked items display
    if (onItemsCreated) {
      onItemsCreated();
    }
  };

  const handleBulkItemComplete = (itemId: string) => {
    handleItemsComplete([itemId]);
  };

  const handleBulkRestockComplete = async (stockId: string) => {
    // Restock completed - Lambda already updated allocation, just refresh UI
    await loadLineItems();
    if (onPurchaseUpdated) onPurchaseUpdated();
    
    // Reset state
    setIsRestockingBulk(false);
    setSelectedLineItem(null);
    setSelectedProduct(null);
    setExistingBulkStock(null);
    
    if (onItemsCreated) onItemsCreated();
  };

  const handleSerializedFromCatalogComplete = async (inventoryIds: string[]) => {
    // Serialized items created - Lambda already updated allocation, just refresh UI
    await loadLineItems();
    if (onPurchaseUpdated) onPurchaseUpdated();
    
    // Reset state
    setIsCreatingSerializedFromCatalog(false);
    setSelectedLineItem(null);
    setSelectedProduct(null);
    
    if (onItemsCreated) onItemsCreated();
  };

  const handleBulkCreateFromCatalogComplete = async (stockId: string) => {
    // Initial stock created - Lambda already updated allocation, just refresh UI
    await loadLineItems();
    if (onPurchaseUpdated) onPurchaseUpdated();
    
    // Reset state
    setIsCreatingBulkFromCatalog(false);
    setSelectedLineItem(null);
    setSelectedProduct(null);
    
    if (onItemsCreated) onItemsCreated();
  };

  if (!isOpen || !purchase) return null;

  const remainingQuantityForLine = selectedLineItem 
    ? selectedLineItem.quantity - selectedLineItem.allocatedQuantity 
    : 0;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Purchase Details</h2>
              <p className="text-sm text-gray-600 mt-1">
                {purchase.vendor || 'Purchase Record'} - {purchase.purchaseDate}
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Purchase Header Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Purchase Date</p>
                  <p className="text-sm text-gray-900">{purchase.purchaseDate}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Total Cost</p>
                  <p className="text-sm text-gray-900">
                    ${purchase.totalCost.toFixed(2)}
                  </p>
                </div>
              </div>

              {purchase.vendor && (
                <div className="flex items-start space-x-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Vendor</p>
                    <p className="text-sm text-gray-900">{purchase.vendor}</p>
                  </div>
                </div>
              )}

              {purchase.poNumber && (
                <div className="flex items-start space-x-3">
                  <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">PO Number</p>
                    <p className="text-sm text-gray-900">{purchase.poNumber}</p>
                  </div>
                </div>
              )}

              {purchase.invoiceNumber && (
                <div className="flex items-start space-x-3">
                  <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Invoice Number</p>
                    <p className="text-sm text-gray-900">{purchase.invoiceNumber}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            {purchase.notes && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                  {purchase.notes}
                </p>
              </div>
            )}

            {/* Line Items Section */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Line Items ({lineItems.length})
                </h3>
                <button
                  onClick={handleAddLineItemClick}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Line Item</span>
                </button>
              </div>

              {loadingLineItems ? (
                <div className="text-center py-8 text-gray-500">
                  Loading line items...
                </div>
              ) : lineItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No line items yet. Click "Add Line Item" to add items to this purchase.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Qty
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cost
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Progress
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {lineItems.map((lineItem) => (
                        <tr key={lineItem.lineItemId} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900">
                                {lineItem.itemDescription}
                              </span>
                              {(lineItem as any).productId && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                  Catalog
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {lineItem.categoryName} › {lineItem.subcategoryName}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {lineItem.quantity}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            ${lineItem.totalCost.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <AllocationProgressBar
                              allocated={lineItem.allocatedQuantity}
                              total={lineItem.quantity}
                            />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {lineItem.allocatedQuantity < lineItem.quantity ? (
                              <button
                                onClick={() => handleAddItemsFromLine(lineItem)}
                                className="text-blue-600 hover:text-blue-800 font-medium"
                              >
                                Add Items
                              </button>
                            ) : (
                              <span className="text-green-600 font-medium">✓ Complete</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Linked Inventory Items */}
            {linkedItems.length > 0 && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Items Created from this Purchase ({linkedItems.length})
                </h3>
                <div className="space-y-2">
                  {linkedItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Package className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {item.name || item.itemName}
                          </p>
                          {item.serialNumber && (
                            <p className="text-xs text-gray-600">
                              Serial: {item.serialNumber}
                            </p>
                          )}
                          {item.itemType !== 'SERIALIZED' && item.totalQuantity && (
                            <p className="text-xs text-gray-600">
                              Quantity: {item.totalQuantity}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${
                        item.itemType === 'SERIALIZED'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {item.itemType === 'SERIALIZED' ? 'Serialized' : item.itemType === 'CONSUMABLE' ? 'Consumable' : 'Bulk'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex justify-between p-6 border-t border-gray-200 sticky bottom-0 bg-white">
            <div className="flex space-x-2">
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Edit</span>
                </button>
              )}
              {onDelete && (
                <button
                  onClick={onDelete}
                  className="flex items-center space-x-2 px-4 py-2 text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Sub-modals */}
      <AddLineItemModal
        isOpen={isAddingLineItem}
        onClose={() => setIsAddingLineItem(false)}
        purchaseId={purchase.purchaseId}
        categories={categories}
        onLineItemAdded={handleLineItemAdded}
      />

      {selectedLineItem && (
        <>
          <SerializedItemBatchModal
            isOpen={isCreatingSerializedItems}
            onClose={() => {
              setIsCreatingSerializedItems(false);
              setSelectedLineItem(null);
            }}
            purchaseId={purchase.purchaseId}
            lineItemId={selectedLineItem.lineItemId}
            purchaseDate={purchase.purchaseDate}
            subcategoryId={selectedLineItem.subcategoryId}
            subcategoryName={selectedLineItem.subcategoryName || ''}
            suggestedQuantity={remainingQuantityForLine}
            onComplete={handleItemsComplete}
          />

          <BulkItemCreationModal
            isOpen={isCreatingBulkItem}
            onClose={() => {
              setIsCreatingBulkItem(false);
              setSelectedLineItem(null);
            }}
            purchaseId={purchase.purchaseId}
            lineItemId={selectedLineItem.lineItemId}
            purchaseDate={purchase.purchaseDate}
            subcategoryId={selectedLineItem.subcategoryId}
            subcategoryName={selectedLineItem.subcategoryName || ''}
            suggestedQuantity={remainingQuantityForLine}
            onSave={handleBulkItemComplete}
          />
        </>
      )}

      {/* New Catalog-Aware Modals */}
      {selectedLineItem && selectedProduct && existingBulkStock && (
        <BulkRestockModal
          isOpen={isRestockingBulk}
          onClose={() => {
            setIsRestockingBulk(false);
            setSelectedLineItem(null);
            setSelectedProduct(null);
            setExistingBulkStock(null);
          }}
          product={selectedProduct}
          existingStock={existingBulkStock}
          lineItem={selectedLineItem}
          purchaseId={purchase.purchaseId}
          onComplete={handleBulkRestockComplete}
        />
      )}

      {selectedLineItem && selectedProduct && (
        <BulkCreateFromCatalogModal
          isOpen={isCreatingBulkFromCatalog}
          onClose={() => {
            setIsCreatingBulkFromCatalog(false);
            setSelectedLineItem(null);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
          lineItem={selectedLineItem}
          purchaseId={purchase.purchaseId}
          onComplete={handleBulkCreateFromCatalogComplete}
        />
      )}

      {selectedLineItem && selectedProduct && (
        <SerializedFromCatalogModal
          isOpen={isCreatingSerializedFromCatalog}
          onClose={() => {
            setIsCreatingSerializedFromCatalog(false);
            setSelectedLineItem(null);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
          lineItem={selectedLineItem}
          purchaseId={purchase.purchaseId}
          onComplete={handleSerializedFromCatalogComplete}
        />
      )}
    </>
  );
};

export default PurchaseDetailModal;